import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Template for the bulk lead import. Generated rather than shipped as a binary
// asset so the headers can never drift from what the importer parses.
export const downloadLeadImportSample = () => {
  const rows = [
    ['name', 'number', 'city'],
    ['Ramesh Textiles', '9876543210', 'Surat'],
    ['', '9876543211', ''],
  ];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, 'lead-import-sample.xlsx');
};

// Template for the bulk party rate import. Every product is listed so the user
// only has to edit the price column. `currentRates` (productId -> price) is the
// selected party's existing rates: pre-filling them means an untouched row
// re-imports as "no change" instead of resetting that product to its default.
export const downloadPartyRateImportSample = (
  products = [],
  currentRates = {},
) => {
  const rows = [
    ['product', 'price'],
    ...products.map((product) => [
      product?.name ?? '',
      currentRates[product?._id] ?? product?.price ?? 0,
    ]),
  ];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = [{ wch: 40 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Party Rate');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, 'party-rate-import-sample.xlsx');
};

// One list -> one .xlsx, for the "Download Excel" button on a list screen.
//
// `columns` is [{ header, width, value: (row, index) => cell }] so the sheet's
// column order and the header text are declared in one place and cannot drift
// apart. A cell is written as a number when the value is a number, otherwise as
// text - which is what keeps a phone number from losing a leading zero or being
// rewritten in scientific notation.
//
// `filters` is an optional [[label, value]] list. When given, it is written to a
// second sheet, so the file itself says what it was filtered by rather than
// leaving that to whoever receives it.
//
// Note on styling: this project uses the community build of SheetJS, which
// ignores cell styles on write, so there is no bold header to be had here. The
// column widths and the auto-filter on the header row are what it does support,
// and both land correctly in Excel.
export const downloadListExcel = ({
  fileName,
  sheetName = 'Sheet1',
  columns = [],
  rows = [],
  filters = [],
}) => {
  const cell = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return Number.isFinite(value) ? value : '';
    return String(value);
  };

  const header = columns.map((column) => column.header);
  const body = rows.map((row, index) =>
    columns.map((column) => cell(column.value(row, index))),
  );

  const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
  worksheet['!cols'] = columns.map((column) => ({ wch: column.width ?? 18 }));
  // Header row included, so the filter arrows sit on the headings.
  worksheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: body.length, c: Math.max(columns.length - 1, 0) },
    }),
  };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  if (filters.length) {
    const filterSheet = XLSX.utils.aoa_to_sheet([
      ['Filter', 'Applied'],
      ...filters.map(([label, value]) => [label, cell(value)]),
    ]);
    filterSheet['!cols'] = [{ wch: 22 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(workbook, filterSheet, 'Filters');
  }

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};

export const exportToExcel = (exportData) => {
  let level1 = [];
  let level2 = [];
  let level3 = [];

  exportData.forEach((val) => {
    val.variant.forEach((data) => {
      level1.push([
        // val?.status ?? '',
        val?.countryId?.name ?? '',
        // val?.supplier ?? '',
        val?.productName ?? '',
        val?.productModel ?? '',
        val?.brandName ?? '',
        data?.oldMiaSku ?? '',
        data?.miaSku ?? '',
        data?.colorId?.name ?? '',
        data?.sizeId?.name ?? '',
        data?.currency ?? '',
        // val?.cost ?? '',
        // val?.productMargin ?? '',
        data?.miATrade ??
          (data?.cost ?? 0) +
            (data?.cost ?? 0) * ((data?.productMargin ?? 40) / 100) ??
          '',
        // val?.supplierSku ?? '',
        // val?.inventoryLocation ?? '',
      ]);

      level2.push([
        data?.miaSku ?? '',
        val?.productName ?? '',
        val?.printLocation1 ?? '',
        val?.printOverlay1 ?? '',
        val?.printWidth1 ?? '',
        val?.printHeight1 ?? '',
        val?.printDpi1 ?? '',
        val?.printLocation2 ?? '',
        val?.additionalLocation ?? '',
        val?.printOverlay2 ?? '',
        val?.printWidth2 ?? '',
        val?.printHeight2 ?? '',
        val?.printDpi2 ?? '',
        val?.printLocation3 ?? '',
        val?.printOverlay3 ?? '',
        val?.printWidth3 ?? '',
        val?.printHeight3 ?? '',
        val?.printDpi3 ?? '',
        val?.printLocation4 ?? '',
        val?.printOverlay4 ?? '',
        val?.printWidth4 ?? '',
        val?.printHeight4 ?? '',
        val?.printDpi4 ?? '',
        val?.printLocation5 ?? '',
        val?.printOverlay5 ?? '',
        val?.printWidth5 ?? '',
        val?.printHeight5 ?? '',
        val?.printDpi5 ?? '',
        val?.printLocation6 ?? '',
        val?.printOverlay6 ?? '',
        val?.printWidth6 ?? '',
        val?.printHeight6 ?? '',
        val?.printDpi6 ?? '',
        val?.printLocation7 ?? '',
        val?.printOverlay7 ?? '',
        val?.printWidth7 ?? '',
        val?.printHeight7 ?? '',
        val?.printDpi7 ?? '',
        val?.printLocation8 ?? '',
        val?.printOverlay8 ?? '',
        val?.printWidth8 ?? '',
        val?.printHeight8 ?? '',
        val?.printDpi8 ?? '',
        val?.productWeightG ?? '',
        val?.productWeightLbs ?? '',
        val?.productWeightOz ?? '',
        val?.productLength ?? '',
        val?.productWidth ?? '',
        val?.productHeight ?? '',
        val?.productVolume ?? '',
        val?.productSurfaceArea ?? '',
        val?.packageWeightG ?? '',
        val?.packageWeightLbs ?? '',
        val?.packageWeightOz ?? '',
        val?.packageLength ?? '',
        val?.packageWidth ?? '',
        val?.packageHeight ?? '',
        val?.packageVolume ?? '',
        val?.packageSurfaceArea ?? '',
        val?.shippingWeightG ?? '',
        val?.shippingWeightLbs ?? '',
        val?.shippingWeightOz ?? '',
        val?.shippingLength ?? '',
        val?.shippingWidth ?? '',
        val?.shippingHeight ?? '',
        val?.shippingSurfaceArea ?? '',
        // val?.shippingCost ?? '',
        val?.shippingTime ?? '',
        val?.productVideoUrl ?? '',
        val?.productUrl ?? '',
        val?.catalog ?? '',
        val?.leadTime ?? '',
        val?.stockLevel ?? '',
        val?.minOrderQty ?? '',
        val?.maxOrderQty ?? '',
        val?.orderIncrement ?? '',
        val?.availableForOrder ?? '',
        // val?.shippingMargin ?? '',
        val?.productionTimesDays ?? '',
        // val?.description ?? '',
        // val?.mockUpImage ?? '',
        // val?.descriptionOfMockImage ?? '',
        // val?.nameOfMockImage ?? '',
        data?.blankImage ?? '',
        // val?.descriptionOfImage ?? '',
        // val?.nameOfBlankImage ?? '',
        data?.secondaryImage ?? '',
        // val?.descriptionOfImage2 ?? '',
        // val?.nameOfSecondaryImage ?? '',
        data?.thirdImage ?? '',
        // val?.descriptionOfImage3 ?? '',
        // val?.nameOfImage3 ?? '',
        data?.fourthImage ?? '',
        // val?.descriptionOfImage4 ?? '',
        // val?.nameOfImage4 ?? '',
        data?.fifthImage ?? '',
        // val?.descriptionOfImage5 ?? '',
        // val?.nameOfImage5 ?? '',
        data?.lifestyleImage ?? '',
        // val?.descriptionOfLifestyleImage ?? '',
        // val?.nameOfLifestyleImage ?? '',
        data?.collectiveImageOfAllVariations ?? '',
        // val?.descriptionOfCollectiveImageOfAllVariations ?? '',
        // val?.nameOfCollectiveImageOfAllVariations ?? '',
        val?.internationalShippingTax ?? '',
        val?.shippingInternationalTradeAndTaxTotal ?? '',
        // val?.handlingTotal ?? '',
        val?.printTemplate ?? '',
        val?.fileFormat ?? '',
        val?.neckLabelTemplate ?? '',
        // val?.neckLabel ?? '',
      ]); // Example of adding an empty row with 3 columns

      level3.push([
        data?.miaSku ?? '',
        val?.productName ?? '',
        // val?.platformSupplierField ?? '',
        val?.warranty ?? '',
        // val?.condition ?? '',
        // val?.xid ?? '',
        // val?.promoStandardsMembershipNumber ?? '',
        val?.espAsiProductNumber ?? '',
        // val?.appaMembershipNumber ?? '',
        // val?.appaProductCode ?? '',
        // val?.sageItemNumber ?? '',
        // val?.productDataSheet ?? '',
        val?.prop65Applicable ?? '',
        val?.prop65Warnings ?? '',
        val?.prop65Chemicals ?? '',
        val?.prop65AdditionalInfo ?? '',
        // val?.distributorOnly ?? '',
        // val?.updateInventory ?? '',
        val?.inventoryOnHand ?? '',
        val?.language ?? '',
        val?.customsDetails ?? '',
        val?.customsValue ?? '',
        val?.customsDuties ?? '',
        val?.customsTaxes ?? '',
        val?.customsClearanceTime ?? '',
        val?.customsInstructions ?? '',
        val?.complianceCertifications ?? '',
        val?.complianceStandards ?? '',
        val?.complianceReports ?? '',
        val?.complianceWarnings ?? '',
        val?.safetyCertifications ?? '',
        val?.safetyStandards ?? '',
        val?.safetyReports ?? '',
        val?.safetyWarnings ?? '',
        val?.groupImageFileName ?? '',
        val?.reorderLevel ?? '',
        val?.reorderTime ?? '',
      ]);
    });

    // Example of adding an empty row with 3 columns
  });
  // Define data for each sheet
  const sheet1Data = [
    [
      // 'status',
      'country',
      // 'supplier',
      'productName',
      'productModel',
      'brandName',
      'oldMiaSku',
      'miaSku',
      'color',
      'size',
      'currency',
      // 'cost',
      // 'productMargin',
      'miaTrade',
      // 'supplierSku',
      // 'inventoryLocation',
    ],
    ...level1,
  ];

  const sheet2Data = [
    [
      'miaSku',
      'productName',
      'printLocation1',
      'printOverlay1',
      'printWidth1',
      'printHeight1',
      'printDpi1',
      'printLocation2',
      'additionalLocation',
      'printOverlay2',
      'printWidth2',
      'printHeight2',
      'printDpi2',
      'printLocation3',
      'printOverlay3',
      'printWidth3',
      'printHeight3',
      'printDpi3',
      'printLocation4',
      'printOverlay4',
      'printWidth4',
      'printHeight4',
      'printDpi4',
      'printLocation5',
      'printOverlay5',
      'printWidth5',
      'printHeight5',
      'printDpi5',
      'printLocation6',
      'printOverlay6',
      'printWidth6',
      'printHeight6',
      'printDpi6',
      'printLocation7',
      'printOverlay7',
      'printWidth7',
      'printHeight7',
      'printDpi7',
      'printLocation8',
      'printOverlay8',
      'printWidth8',
      'printHeight8',
      'printDpi8',
      'productWeightG',
      'productWeightLbs',
      'productWeightOz',
      'productLength',
      'productWidth',
      'productHeight',
      'productVolume',
      'productSurfaceArea',
      'packageWeightG',
      'packageWeightLbs',
      'packageWeightOz',
      'packageLength',
      'packageWidth',
      'packageHeight',
      'packageVolume',
      'packageSurfaceArea',
      'shippingWeightG',
      'shippingWeightLbs',
      'shippingWeightOz',
      'shippingLength',
      'shippingWidth',
      'shippingHeight',
      'shippingSurfaceArea',
      // 'shippingCost',
      'shippingTime',
      'productVideoUrl',
      'productUrl',
      'catalog',
      'leadTime',
      'stockLevel',
      'minOrderQty',
      'maxOrderQty',
      'orderIncrement',
      'availableForOrder',
      // 'shippingMargin',
      'productionTimesDays',
      // 'description',
      // 'mockUpImage',
      // 'descriptionOfMockImage',
      // 'nameOfMockImage',
      'blankImage',
      // 'descriptionOfImage',
      // 'nameOfBlankImage',
      'secondaryImage',
      // 'descriptionOfImage2',
      // 'nameOfSecondaryImage',
      'thirdImage',
      // 'descriptionOfImage3',
      // 'nameOfImage3',
      'fourthImage',
      // 'descriptionOfImage4',
      // 'nameOfImage4',
      'fifthImage',
      // 'descriptionOfImage5',
      // 'nameOfImage5',
      'lifestyleImage',
      // 'descriptionOfLifestyleImage',
      // 'nameOfLifestyleImage',
      'collectiveImageOfAllVariations',
      // 'descriptionOfCollectiveImageOfAllVariations',
      // 'nameOfCollectiveImageOfAllVariations',
      'internationalShippingTax',
      'shippingInternationalTradeAndTaxTotal',
      // 'handlingTotal',
      'printTemplate',
      'fileFormat',
      'neckLabelTemplate',
      // 'neckLabel',
    ],
    ...level2,
  ];

  const sheet3Data = [
    [
      'miaSku',
      'productName',
      // 'platformSupplierField',
      'warranty',
      // 'condition',
      // 'xid',
      // 'promoStandardsMembershipNumber',
      'espAsiProductNumber',
      // 'appaMembershipNumber',
      // 'appaProductCode',
      // 'sageItemNumber',
      // 'productDataSheet',
      'prop65Applicable',
      'prop65Warnings',
      'prop65Chemicals',
      'prop65AdditionalInfo',
      // 'distributorOnly',
      // 'updateInventory',
      'inventoryOnHand',
      'language',
      'customsDetails',
      'customsValue',
      'customsDuties',
      'customsTaxes',
      'customsClearanceTime',
      'customsInstructions',
      'complianceCertifications',
      'complianceStandards',
      'complianceReports',
      'complianceWarnings',
      'safetyCertifications',
      'safetyStandards',
      'safetyReports',
      'safetyWarnings',
      'groupImageFileName',
      'reorderLevel',
      'reorderTime',
    ],
    ...level3,
  ];

  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Convert each data array to a sheet and append it to the workbook
  const worksheet1 = XLSX.utils.aoa_to_sheet(sheet1Data);
  const worksheet2 = XLSX.utils.aoa_to_sheet(sheet2Data);
  const worksheet3 = XLSX.utils.aoa_to_sheet(sheet3Data);

  // // Apply styles to header cells for worksheet1
  // const headerRange1 = XLSX.utils.decode_range(worksheet1['!ref']);
  // for (let C = headerRange1.s.c; C <= headerRange1.e.c; ++C) {
  //   const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C }); // Row 0, Column C
  //   if (!worksheet1[cellAddress]) continue;
  //   worksheet1[cellAddress].s = {
  //     font: { bold: true, color: { rgb: 'FFFFFF' } }, // Bold, white text
  //     fill: { fgColor: { rgb: '000000' } }, // Dark background
  //   };
  // }

  // Add sheets to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet1, 'Level1');
  XLSX.utils.book_append_sheet(workbook, worksheet2, 'Level2');
  XLSX.utils.book_append_sheet(workbook, worksheet3, 'Level3');

  // Write the workbook and convert it to a Blob
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

  // Save the file
  saveAs(blob, 'blank-products-list.xlsx');
};

export const exportToExcelVirtual = (exportData) => {
  let level1 = [];
  exportData.forEach((val) => {
    level1.push([
      val?.productVariant?.miaSku ?? '',
      val?.productVariant?.blankProduct?.productName ?? '',
      val?.inventoryLocation ?? '',
      val?.retailSku ?? '',
      val?.decorationDescription ?? '',
      val?.designId ?? '',
      val?.mockupImage ?? '',
      val?.secondaryImage ?? '',
      val?.thirdImage ?? '',
      val?.fourthImage ?? '',
      val?.fifthImage ?? '',
      val?.lifestyleImage ?? '',
      val?.collectiveImage ?? '',
      val?.printLocation1 ?? '',
      val?.printUrl1 ?? '',
      val?.printLocation2 ?? '',
      val?.printUrl2 ?? '',
      val?.printLocation3 ?? '',
      val?.printUrl3 ?? '',
      val?.printLocation4 ?? '',
      val?.printUrl4 ?? '',
      val?.printLocation5 ?? '',
      val?.printUrl5 ?? '',
      val?.printLocation6 ?? '',
      val?.printUrl6 ?? '',
      val?.printLocation7 ?? '',
      val?.printUrl7 ?? '',
      val?.printLocation8 ?? '',
      val?.printUrl8 ?? '',
      val?.printType ?? '',
      val?.embroideryDesignId ?? '',
      val?.neckLabelUrl ?? '',
      val?.pricing ?? '',
      val?.ShippingCost ?? '',
      val?.productionTime ?? '',
    ]);
  });
  // Define data for each sheet
  const sheet1Data = [
    [
      'miaSku',
      'productName',
      'inventoryLocation',
      'retailSku',
      'decorationDescription',
      'designId',
      'mockupImage',
      'secondaryImage',
      'thirdImage',
      'fourthImage',
      'fifthImage',
      'lifestyleImage',
      'collectiveImage',
      'printLocation1',
      'printUrl1',
      'printLocation2',
      'printUrl2',
      'printLocation3',
      'printUrl3',
      'printLocation4',
      'printUrl4',
      'printLocation5',
      'printUrl5',
      'printLocation6',
      'printUrl6',
      'printLocation7',
      'printUrl7',
      'printLocation8',
      'printUrl8',
      'printType',
      'embroideryDesignId',
      'neckLabelUrl',
      'pricing',
      'ShippingCost',
      'productionTime',
    ],
    ...level1,
  ];
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  // Convert each data array to a sheet and append it to the workbook
  const worksheet1 = XLSX.utils.aoa_to_sheet(sheet1Data);
  // Add sheets to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet1, 'Sheet1');
  // Write the workbook and convert it to a Blob
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  // Save the file
  saveAs(blob, 'virtual-products-list.xlsx');
};
